/*
 * Copyright (c) Nexign, JSC, 2020-2023.
 */
/**
 * DatePicker standard implementation
 * @see DropDownFilter - datepicker wrapper for open\close container and show input with date
 * @see DatePickerHeaderSingle - header component implementation
 * @see DatePickerBody = body component implementation
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @version 1.25.1
 * @since 1.10.0
 */

import { useEffect, useRef, useState } from 'react';
import type { FC, ChangeEvent, KeyboardEvent, RefObject } from 'react';

import moment, { Moment } from 'moment';
import InputMask from 'react-input-mask';

import { icons } from '@assets/index';
import { KeyboardKeyEnum } from '@modules/app/appConstants';
import { ICommonTranslation } from '@modules/internationalization/types';
import { EFormatData } from '@pages/PageDashboard/types/widgetTypes';

import { DropDownFilter as DropDownFilterComponent, DropDownFilterButtonSize } from '../DropDownFilter/DropDownFilter';
import { DropDownFilter, Input } from '../index';
import { EInputKind, EInputType } from '../Input/Input';
import DatePickerBody from './components/Body/DatePickerBody';
import DatePickerHeaderSeparate from './components/Header/DatePickerHeaderSeparate';
import DatePickerHeaderSingle from './components/Header/DatePickerHeaderSingle';
import { DatePickerDropdownStyled, DatePickerStyled } from './styled/DatePickerStyled';
import { DatePickerFooterItemStyled, DatePickerFooterStyled } from './styled/Footer/DatePickerFooterStyled';
import { EDatePickerPeriodType, IDatePickerAdditionalButton, IDatePickerLocale } from './types/datePickerTypes';

export interface IDatePickerProps {
    additionalButtons?: IDatePickerAdditionalButton[];
    dataName?: string;
    disabled?: boolean;
    filterButtonSize?: DropDownFilterButtonSize;
    headType?: 'single' | 'separate';
    isCurrentPeriod?: boolean;
    kind?: EInputKind;
    label?: string;
    maxDate?: Moment;
    minDate?: Moment;
    onCloseClick?: () => void;
    onSelectDate: (date: Moment) => void;
    selectedDate?: Moment;
    translation: ICommonTranslation;
}

/**
 * This implementation supports only day period.
 * DatePickerBody component supports other date periods
 */
const periodType = EDatePickerPeriodType.DAY;

const DatePicker: FC<IDatePickerProps> = (props) => {
    const {
        additionalButtons,
        dataName,
        disabled,
        headType,
        isCurrentPeriod,
        label,
        maxDate,
        minDate,
        onSelectDate,
        selectedDate,
        translation,
    } = props;

    const dropDownRef: RefObject<DropDownFilterComponent> = useRef<DropDownFilterComponent>(null);

    const [period, setPeriod] = useState<Moment>(moment());
    const [inputValue, setInputValue] = useState<string>('');

    const locale: IDatePickerLocale = {
        monthNames: translation.calendar.months,
        weekNames: translation.calendar.weeks,
    };

    useEffect(() => {
        setPeriod(selectedDate || (!isCurrentPeriod && maxDate) || (!isCurrentPeriod && minDate) || moment());
    }, [isCurrentPeriod, maxDate, minDate, selectedDate]);

    useEffect(() => {
        setInputValue(selectedDate?.format(EFormatData.FullData) ?? '');
    }, [selectedDate]);

    /*
     * -------------------------------------------
     *               Handlers
     * -------------------------------------------
     */

    const selectDateHandler = (day: Moment) => {
        onSelectDate(day);
        dropDownRef.current?.closeTooltip();
    };

    const footerItemClickHandler = (item: IDatePickerAdditionalButton) => {
        item.onClick();
        dropDownRef.current?.closeTooltip();
    };

    const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.target.value);
    };

    const onInputKeyDownHandler = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === KeyboardKeyEnum.ENTER || event.key === KeyboardKeyEnum.TAB) {
            onCloseClick();
            dropDownRef.current?.closeTooltip();
            event.currentTarget.blur();
        }
    };

    const onInputBlur = () => {
        if (!dropDownRef.current?.state.isToolTipShown) {
            onCloseClick();
        }
    };

    const onCloseClick = () => {
        const date = moment(inputValue, EFormatData.FullData, true);
        const isValid =
            date.isValid() &&
            (!minDate || date.startOf('day').isSameOrAfter(minDate.startOf('day'))) &&
            (!maxDate || date.startOf('day').isSameOrBefore(maxDate.startOf('day')));

        // при закрытии выпадающего меню, если введенная дата валидна, то сохраняем значение
        if (isValid) {
            onSelectDate(date);
        } else {
            setInputValue(selectedDate?.format(EFormatData.FullData) ?? '');
        }

        props.onCloseClick?.();
    };
    /*
     * -------------------------------------------
     *           Render component
     * -------------------------------------------
     */

    const renderButton = () => (
        <Input righticon={icons.calendarIcon} type={EInputType.small}>
            <InputMask
                disabled={props.disabled}
                mask="99.99.9999"
                placeholder={label}
                role="textbox"
                value={inputValue}
                onBlur={onInputBlur}
                onChange={onInputChange}
                onKeyDown={onInputKeyDownHandler}
            />
        </Input>
    );

    return (
        <DatePickerDropdownStyled data-name={dataName || ''}>
            <DropDownFilter
                widthAuto
                closeOnFilterButtonClick={false}
                disabled={disabled}
                filterButtonLabel={inputValue}
                ref={dropDownRef}
                renderButton={renderButton}
                translation={translation.dropDown}
                onCloseClick={onCloseClick}
            >
                <DatePickerStyled>
                    {headType === 'separate' ? (
                        <DatePickerHeaderSeparate
                            locale={locale}
                            maxDate={maxDate}
                            minDate={minDate}
                            period={period}
                            periodChangeHandler={setPeriod}
                        />
                    ) : (
                        <DatePickerHeaderSingle
                            locale={locale}
                            maxDate={maxDate}
                            minDate={minDate}
                            period={period}
                            periodChangeHandler={setPeriod}
                        />
                    )}

                    <DatePickerBody
                        maxDate={maxDate}
                        minDate={minDate}
                        period={period}
                        periodType={periodType}
                        selectedDay={selectedDate}
                        onSelectDateHandler={selectDateHandler}
                    />
                    {!!additionalButtons && (
                        <DatePickerFooterStyled>
                            {additionalButtons.map((button: IDatePickerAdditionalButton) => (
                                <DatePickerFooterItemStyled
                                    key={button.title}
                                    onClick={() => footerItemClickHandler(button)}
                                >
                                    {button.title}
                                </DatePickerFooterItemStyled>
                            ))}
                        </DatePickerFooterStyled>
                    )}
                </DatePickerStyled>
            </DropDownFilter>
        </DatePickerDropdownStyled>
    );
};

export default DatePicker;
